/**
 * Email body helpers.
 *
 * Every email the app drafts is sent as HTML (see
 * `api/services/gmail_service.py` — `build_body_parts`). A body that already
 * contains markup is sent verbatim; a plain-text body is escaped and its
 * newlines become <br>. These helpers mirror that backend logic so the preview
 * in the UI shows what the recipient will actually get.
 *
 * Keep `looksLikeHtml` / `hasOnlyInlineHtml` / `textToHtml` /
 * `inlineHtmlToHtml` in sync with their Python counterparts.
 */

// Same tag list as _HTML_TAG_RE in gmail_service.py. Deliberately a fixed list
// rather than /<[^>]+>/ so a plain-text body containing a placeholder like
// "<name>" is not mistaken for markup.
const HTML_TAG_RE = /<\/?(?:a|b|blockquote|body|br|center|code|div|em|font|h[1-6]|head|hr|html|i|img|li|ol|p|pre|span|strong|style|table|tbody|td|th|thead|tr|u|ul)\b[^>]*>/i;

// Same pattern, but global and capturing the tag name, for the walk in
// `hasOnlyInlineHtml` / `inlineHtmlToHtml`. Kept separate from HTML_TAG_RE on
// purpose: a /g/ regex carries lastIndex between calls, which would make
// `looksLikeHtml`'s .test() return alternating results on the same input.
const HTML_TAG_G_RE = /<\/?(a|b|blockquote|body|br|center|code|div|em|font|h[1-6]|head|hr|html|i|img|li|ol|p|pre|span|strong|style|table|tbody|td|th|thead|tr|u|ul)\b[^>]*>/gi;

// Matches _INLINE_FORMAT_TAGS in gmail_service.py. `br`, `p` and `div` are
// deliberately absent — see the comment there.
const INLINE_FORMAT_TAGS = new Set(['a', 'b', 'em', 'font', 'i', 'span', 'strong', 'u']);

// Matches BODY_FONT_STYLE in gmail_service.py.
export const BODY_FONT_STYLE =
  'font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6;';

/** True when `body` already carries HTML markup that should be sent verbatim. */
export const looksLikeHtml = (body) => Boolean(body) && HTML_TAG_RE.test(body);

/** Escape text so it can be embedded in HTML. */
export const escapeHtml = (text) => String(text ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

/**
 * True when `body` uses inline formatting tags and no structural markup, i.e.
 * plain text that happens to carry a few <strong> / <em> runs. Such a body
 * still gets newline conversion and the font wrapper; a verbatim one does not.
 */
export const hasOnlyInlineHtml = (body) => {
  const names = [...String(body ?? '').matchAll(HTML_TAG_G_RE)]
    .map((m) => m[1].toLowerCase());
  return names.length > 0 && names.every((name) => INLINE_FORMAT_TAGS.has(name));
};

/** Render plain text as HTML: escape it, then map newlines to <br>. */
export const textToHtml = (body) =>
  `<div style="${BODY_FONT_STYLE}">${escapeHtml(body).replace(/\n/g, '<br>')}</div>`;

/**
 * Convert the two markdown emphasis markers into inline HTML tags.
 *
 * FRONTEND-ONLY, AND DELIBERATELY SCOPED TO ONE MODAL. There is no Python
 * counterpart and there must not be: `text_to_html` is shared by every email
 * the app drafts, and `invoicing/document_generator.py` wraps the JP
 * payment-reminder signature in runs of 28 asterisks that any `*` rule would
 * shred. Converting here instead means only bodies composed in
 * ClientEmailDraftModal are ever parsed as markdown.
 *
 * Output is inline HTML, deliberately left unescaped elsewhere: pass the result
 * to `inlineHtmlToHtml` / `toPreviewHtml`, which escape the text around the
 * tags it produced.
 *
 * Both rules require the delimiter to sit against a character that is neither
 * whitespace NOR another asterisk, which is what keeps two things literal: the
 * EN/TH templates' footnote line ("* This email is sent to...", a "* " is never
 * an opener) and a run of asterisks used as a horizontal rule ("****...", as in
 * the JP payment-reminder signature). Emphasis cannot span a newline either, so
 * an unclosed marker degrades to a plain asterisk instead of swallowing the
 * rest of the body.
 */
const EMPHASIS_BODY = '([^*\\n]*?[^*\\s])';
export const markdownToInlineHtml = (body) => String(body ?? '')
  // Bold first: otherwise the italic rule would claim the outer pair of a `**`.
  .replace(new RegExp(`\\*\\*(?=[^*\\s])${EMPHASIS_BODY}\\*\\*`, 'g'), '<strong>$1</strong>')
  .replace(new RegExp(`\\*(?=[^*\\s])${EMPHASIS_BODY}\\*`, 'g'), '<em>$1</em>');

/**
 * Template text -> the HTML the rich-text email editor loads.
 *
 * The templates (ClientEmailDraftModal's getEmailTemplate) are plain text with
 * markdown emphasis (**bold**, *italic*). EVERY LINE becomes its own <p>, and a
 * blank line becomes an empty <p> -- the Gmail/Slack line model, where Enter is
 * a new line. It must not be "blank line = paragraph, single \n = <br>": lists
 * (and any block command) act on whole paragraphs, so lines joined by <br>
 * would all be pulled into a list together when only one was selected.
 *
 * Emphasis goes through markdownToInlineHtml -- deliberately NOT a general
 * markdown parser, which would read the templates' "* This email is sent..."
 * footnote as a bullet list. Text is escaped first, so the only tags in the
 * result are the ones produced here.
 */
export const textToEditorHtml = (text) => {
  const normalized = String(text ?? '').replace(/\r\n?/g, '\n').replace(/^\n+|\n+$/g, '');
  if (!normalized.trim()) return '';
  return normalized
    .split('\n')
    .map((line) => `<p>${markdownToInlineHtml(escapeHtml(line))}</p>`)
    .join('');
};

// Spacing for the block tags the editor emits -- line-based, like Gmail: every
// line is its own zero-margin <p>, and the only vertical space is an explicit
// blank line (an empty <p>, kept open with a <br>). One blank line is exactly
// one line-height, the same gap the old paragraph margin and the plain-text
// path's \n\n produce. Inlined because Gmail strips <style> blocks and every
// mail client applies its own default <p>/<ul> margins.
const EMAIL_BLOCK_STYLE = 'margin:0;';
const EMAIL_LIST_STYLE = 'margin:0;padding-left:24px;';

// RichTextEditor `contentSx` for email bodies: the same font, size,
// line-height and (zero) block spacing that BODY_FONT_STYLE and
// editorHtmlToEmailHtml give the sent email, so what staff type looks like
// what the client receives. Keep in step with the two constants above.
export const EMAIL_EDITOR_CONTENT_SX = {
  fontFamily: 'Arial, Helvetica, sans-serif',
  fontSize: '14px',
  lineHeight: 1.6,
  '& p': { m: 0 },
  '& ul, & ol': { m: 0, pl: '24px' },
};

/**
 * Rich-text editor HTML -> the email body that is saved and sent.
 *
 * Adds inline spacing and the font wrapper, and keeps author-typed blank
 * lines open: an empty <p></p> has no height in a mail client, so it gets a
 * <br>. Every line is its own zero-margin paragraph (Gmail-style), so those
 * blank lines are the only vertical spacing. The result contains <div>/<p>, so
 * gmail_service.build_body_parts sends it VERBATIM -- which is why the layout
 * has to be complete here. Returns '' when the editor holds no text, so an
 * empty body stays empty.
 */
export const editorHtmlToEmailHtml = (html) => {
  const doc = new DOMParser().parseFromString(`<div>${html ?? ''}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root || !root.textContent.trim()) return '';
  // TipTap's trailingNode keeps an empty paragraph after a list that ends the
  // document (so the caret can leave the list). In an email it would just be a
  // stray blank line, so trailing empties go; blank lines mid-body stay.
  while (root.lastElementChild?.tagName === 'P' && !root.lastElementChild.textContent.trim()) {
    root.lastElementChild.remove();
  }
  root.querySelectorAll('p').forEach((paragraph) => {
    paragraph.setAttribute('style', EMAIL_BLOCK_STYLE);
    if (!paragraph.textContent && !paragraph.querySelector('br')) {
      paragraph.appendChild(doc.createElement('br'));
    }
  });
  root.querySelectorAll('ul, ol').forEach((list) => list.setAttribute('style', EMAIL_LIST_STYLE));
  root.querySelectorAll('li').forEach((item) => item.setAttribute('style', EMAIL_BLOCK_STYLE));
  root.setAttribute('style', BODY_FONT_STYLE);
  return root.outerHTML;
};

/**
 * Render a body that mixes plain text with inline formatting tags: escape only
 * the text BETWEEN the tags, keep the tags live, then map newlines to <br>.
 */
export const inlineHtmlToHtml = (body) => {
  const text = String(body ?? '');
  let out = '';
  let cursor = 0;
  for (const match of text.matchAll(HTML_TAG_G_RE)) {
    out += escapeHtml(text.slice(cursor, match.index)) + match[0];
    cursor = match.index + match[0].length;
  }
  out += escapeHtml(text.slice(cursor));
  return `<div style="${BODY_FONT_STYLE}">${out.replace(/\n/g, '<br>')}</div>`;
};

/**
 * Strip the parts of an HTML body that must never run in our own page.
 *
 * Draft bodies are written by staff, not the public, so this is defence in
 * depth rather than a trust boundary — but the preview injects them with
 * dangerouslySetInnerHTML, so active content is removed first.
 */
export const sanitizeEmailHtml = (html) => String(html ?? '')
  .replace(/<\s*(script|style|iframe|object|embed|link|meta)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
  .replace(/<\s*(script|style|iframe|object|embed|link|meta)\b[^>]*\/?>/gi, '')
  .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, '')
  .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, '')
  .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
  .replace(/(href|src)\s*=\s*(["'])\s*javascript:[^"']*\2/gi, '$1="#"');

/**
 * Turn a stored draft body into HTML safe to drop into the preview pane.
 * Mirrors what `build_body_parts` will send — same branch order, so the inline
 * case is tested before the verbatim one.
 */
export const toPreviewHtml = (body) => {
  if (hasOnlyInlineHtml(body)) return sanitizeEmailHtml(inlineHtmlToHtml(body));
  return sanitizeEmailHtml(looksLikeHtml(body) ? body : textToHtml(body));
};
